import { useLoader } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import {
  BufferGeometry,
  DirectionalLight,
  NoColorSpace,
  Object3D,
  SRGBColorSpace,
  TextureLoader,
  Vector3 as ThreeVector3,
} from "three";
import { MOON_RADIUS } from "../simulation/constants";
import type { SimulationSnapshot } from "../simulation/types";
import { LabelSprite } from "./LabelSprite";
import { earthshineIntensity } from "./sceneLighting";
import { synchronousMoonQuaternion } from "./sceneMath";

export function Moon({
  snapshot,
  showRotationMarker,
  showTerminator,
  classroomMode,
}: {
  readonly snapshot: SimulationSnapshot;
  readonly showRotationMarker: boolean;
  readonly showTerminator: boolean;
  readonly classroomMode: boolean;
}) {
  const sourceTexture = useLoader(TextureLoader, "/textures/moon-lroc.jpg");
  const texture = useMemo(() => {
    const nextTexture = sourceTexture.clone();
    nextTexture.anisotropy = 8;
    nextTexture.colorSpace = SRGBColorSpace;
    nextTexture.needsUpdate = true;
    return nextTexture;
  }, [sourceTexture]);
  const bumpMap = useMemo(() => {
    const nextTexture = sourceTexture.clone();
    nextTexture.anisotropy = 8;
    nextTexture.colorSpace = NoColorSpace;
    nextTexture.needsUpdate = true;
    return nextTexture;
  }, [sourceTexture]);
  const earthshine = useRef<DirectionalLight>(null);
  const earthshineTarget = useRef<Object3D>(null);
  const earthshineStrength = earthshineIntensity(snapshot.illuminatedFraction);
  const quaternion = useMemo(
    () =>
      synchronousMoonQuaternion(snapshot.moonToEarth, snapshot.orbitalNormal),
    [snapshot.moonToEarth, snapshot.orbitalNormal],
  );
  // Sunlight always travels +x, so the terminator circle lies in the y–z plane.
  const terminatorGeometry = useMemo(() => {
    const points = Array.from({ length: 97 }, (_, index) => {
      const angle = (index / 96) * Math.PI * 2;
      return new ThreeVector3(
        0,
        Math.cos(angle),
        Math.sin(angle),
      ).multiplyScalar(MOON_RADIUS * 1.02);
    });
    return new BufferGeometry().setFromPoints(points);
  }, []);

  useEffect(() => () => texture.dispose(), [texture]);
  useEffect(() => () => bumpMap.dispose(), [bumpMap]);
  useEffect(() => () => terminatorGeometry.dispose(), [terminatorGeometry]);
  useEffect(() => {
    if (earthshine.current !== null && earthshineTarget.current !== null) {
      earthshine.current.target = earthshineTarget.current;
      earthshineTarget.current.updateMatrixWorld();
    }
  }, []);

  return (
    <group position={snapshot.moonPosition}>
      <directionalLight
        ref={earthshine}
        color="#8fb6ff"
        intensity={earthshineStrength}
        position={snapshot.moonToEarth}
      />
      <object3D ref={earthshineTarget} />
      <group quaternion={quaternion}>
        <mesh>
          <sphereGeometry args={[MOON_RADIUS, 64, 32]} />
          <meshStandardMaterial
            bumpMap={bumpMap}
            bumpScale={0.018}
            map={texture}
            roughness={0.94}
          />
        </mesh>
        {showRotationMarker ? (
          // Fixed to the Moon's surface on the hemisphere that always faces Earth
          <mesh position={[0, 0, MOON_RADIUS * 0.99]}>
            <sphereGeometry args={[MOON_RADIUS * 0.09, 16, 16]} />
            <meshBasicMaterial color="#ff3b30" toneMapped={false} />
          </mesh>
        ) : null}
      </group>
      {showTerminator ? (
        <lineLoop geometry={terminatorGeometry}>
          <lineBasicMaterial color="#ffd166" transparent opacity={0.7} />
        </lineLoop>
      ) : null}
      <LabelSprite
        text={classroomMode ? "Moon — the ball on a stick" : "Moon"}
        position={[0, -(MOON_RADIUS + 0.42), 0]}
        scale={classroomMode ? [3.6, 0.5, 1] : [1.5, 0.5, 1]}
      />
    </group>
  );
}
